import type { IntlShape } from 'react-intl';

import type { BlockStatus } from '../../../../data/projects/timeline/presentation/types';
import { workingBlockMessages } from '../../../../data/projects/timeline/working-block/workingBlock.messages';
import type { LineState } from '../../../../data/projects/timeline/working-line/lineState';
import type {
  RowPhase,
  WorkRow,
} from '../../../../data/projects/timeline/working-line/workRows';
import { inlineToolCallLabel } from '../inlineToolCallLabel';

const SECONDS_PER_MINUTE = 60;

/** `45s` · `2m` · `2m 30s` — the demo's ladder, translated. */
export const formatWorkingDuration = (intl: IntlShape, totalSeconds: number): string => {
  const safeSeconds = Math.max(0, Math.round(totalSeconds));
  if (safeSeconds < SECONDS_PER_MINUTE) {
    return intl.formatMessage(workingBlockMessages.durationSeconds, {
      seconds: safeSeconds,
    });
  }
  const minutes = Math.floor(safeSeconds / SECONDS_PER_MINUTE);
  const seconds = safeSeconds % SECONDS_PER_MINUTE;
  return seconds === 0
    ? intl.formatMessage(workingBlockMessages.durationMinutes, { minutes })
    : intl.formatMessage(workingBlockMessages.durationMinutesSeconds, {
        minutes,
        seconds,
      });
};

const settledStatus: Record<RowPhase, BlockStatus> = {
  preparing: 'running',
  running: 'running',
  succeeded: 'success',
  failed: 'error',
  cancelled: 'cancelled',
};

/**
 * The row's own label, from the inputs its row renders with — a single call's
 * input, or a group's visible count and successes — so the two never disagree.
 */
const rowLabel = (intl: IntlShape, row: WorkRow, tense: 'active' | 'past'): string => {
  const status = tense === 'active' ? 'running' : settledStatus[row.phase];
  const toolCallDisplay = row.item.toolCallDisplay
    ? { toolCallDisplay: row.item.toolCallDisplay }
    : {};
  return row.count > 1
    ? inlineToolCallLabel(intl, {
        toolName: row.item.name,
        status,
        ...toolCallDisplay,
        count: row.count,
        done: row.done,
      })
    : inlineToolCallLabel(intl, {
        toolName: row.item.name,
        status,
        input: row.item.input,
        ...toolCallDisplay,
      });
};

const durationLine = (intl: IntlShape, seconds: number | null, live: boolean): string =>
  seconds === null
    ? intl.formatMessage(
        live ? workingBlockMessages.thinking : workingBlockMessages.thought
      )
    : intl.formatMessage(
        live ? workingBlockMessages.thinkingFor : workingBlockMessages.thoughtFor,
        { duration: formatWorkingDuration(intl, seconds) }
      );

/** The only place a Working block's `LineState` becomes words. */
export const formatWorkingLine = (intl: IntlShape, state: LineState): string => {
  switch (state.kind) {
    case 'working':
      return durationLine(intl, state.seconds, true);
    // `WorkingBlock` rotates the phrases; as words, the line is the first.
    case 'phrases':
      return state.phrases[0] ?? '';
    case 'row':
      return rowLabel(intl, state.row, state.tense);
    case 'settled': {
      const { summary } = state;
      switch (summary.form) {
        case 'yield':
          return intl.formatMessage(workingBlockMessages.workedThroughSources, {
            count: summary.count,
          });
        case 'steps':
          return intl.formatMessage(workingBlockMessages.workedThroughSteps, {
            count: summary.count,
          });
        case 'row':
          return rowLabel(intl, summary.row, 'past');
        case 'duration':
          return durationLine(intl, summary.seconds, false);
      }
    }
  }
};
